import { useState, useEffect } from 'react';
import { FiX, FiCheck } from 'react-icons/fi';

/**
 * Admin Feature Selection Modal
 * Allows admin to select which advance plan features to enable for a trainer
 * 
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {Function} onClose - Callback to close the modal
 * @param {Function} onSave - Callback when save is clicked, receives (features) object
 * @param {Object} currentFeatures - Current feature flags from trainer (optional)
 */
const AdminFeatureModal = ({ isOpen, onClose, onSave, currentFeatures = null }) => {
  const [features, setFeatures] = useState({
    showHideTestimonials: false,
    increasedClassCapacity: false,
    multipleServices: false,
    whatsappMessaging: false,
    referralCodeSystem: false,
  });

  // Initialize features from currentFeatures if provided
  // If no currentFeatures (upgrading to advance), enable all features by default
  useEffect(() => {
    if (currentFeatures) {
      // Managing existing advance trainer - use current feature state
      setFeatures({
        showHideTestimonials: currentFeatures.showHideTestimonials || false,
        increasedClassCapacity: currentFeatures.increasedClassCapacity || false,
        multipleServices: currentFeatures.multipleServices || false,
        whatsappMessaging: currentFeatures.whatsappMessaging || false,
        referralCodeSystem: currentFeatures.referralCodeSystem || false,
      });
    } else {
      // Upgrading to advance - enable all features by default
      setFeatures({
        showHideTestimonials: true,
        increasedClassCapacity: true,
        multipleServices: true,
        whatsappMessaging: true,
        referralCodeSystem: true,
      });
    }
  }, [currentFeatures, isOpen]);

  if (!isOpen) return null;

  const featureList = [
    {
      key: 'showHideTestimonials',
      title: 'Show/Hide Testimonials',
      description: 'Allow trainers to control visibility of student reviews and testimonials',
    },
    {
      key: 'increasedClassCapacity',
      title: 'Increased Class Capacity',
      description: 'Allow up to 50 students per class (free plan limited to 10)',
    },
    {
      key: 'multipleServices',
      title: 'Multiple Services Support',
      description: 'Enable trainers to offer multiple services simultaneously',
    },
    {
      key: 'whatsappMessaging',
      title: 'WhatsApp Messaging',
      description: 'Enable WhatsApp notifications for student approvals and payment reminders',
    },
    {
      key: 'referralCodeSystem',
      title: 'Referral Code System',
      description: 'Enable referral code generation and management for trainers. Students can share codes and earn discounts.',
    },
  ];

  const handleToggle = (key) => {
    setFeatures(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSelectAll = () => {
    setFeatures({
      showHideTestimonials: true,
      increasedClassCapacity: true,
      multipleServices: true,
      whatsappMessaging: true,
      referralCodeSystem: true,
    });
  };

  const handleDeselectAll = () => {
    setFeatures({
      showHideTestimonials: false,
      increasedClassCapacity: false,
      multipleServices: false,
      whatsappMessaging: false,
      referralCodeSystem: false,
    });
  };

  const allSelected = Object.values(features).every(v => v === true);
  const noneSelected = Object.values(features).every(v => v === false);

  const handleSave = () => {
    onSave(features);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-900">
            Select Advance Plan Features
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <FiX size={24} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600 mb-4">
            {currentFeatures 
              ? 'Select which features to enable for this trainer. You can enable all features or choose specific ones.'
              : 'All advance features are enabled by default. You can uncheck any features you want to disable.'}
          </p>

          {/* Select All / Deselect All buttons */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={handleSelectAll}
              disabled={allSelected}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                allSelected
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
              }`}
            >
              Select All
            </button>
            <button
              onClick={handleDeselectAll}
              disabled={noneSelected}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                noneSelected
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              Deselect All
            </button>
          </div>

          {/* Feature checkboxes */}
          <div className="space-y-3">
            {featureList.map((feature) => (
              <div
                key={feature.key}
                className={`border rounded-lg p-4 transition-colors ${
                  features[feature.key]
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <label className="flex items-start cursor-pointer">
                  <div className="flex-shrink-0 mt-1">
                    <input
                      type="checkbox"
                      checked={features[feature.key]}
                      onChange={() => handleToggle(feature.key)}
                      className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                  </div>
                  <div className="ml-3 flex-1">
                    <div className="flex items-center">
                      <span className="text-base font-medium text-gray-900">
                        {feature.title}
                      </span>
                      {features[feature.key] && (
                        <FiCheck className="ml-2 text-blue-600" size={18} />
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-600">
                      {feature.description}
                    </p>
                  </div>
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Footer buttons */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Save Features
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminFeatureModal;

